import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  AlertTriangle,
  AlertCircle,
  Info,
  TrendingDown,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { formatCurrency } from "../lib/utils";

const API = window.location.origin;

const SEVERITY_STYLES = {
  critical: {
    icon: AlertCircle,
    cardClass: "border-red-500/40 bg-red-950/30",
    iconClass: "text-red-400",
    badgeClass: "bg-red-500/20 text-red-300 border-red-500/40",
    label: "حرج",
  },
  warning: {
    icon: AlertTriangle,
    cardClass: "border-amber-500/40 bg-amber-950/30",
    iconClass: "text-amber-400",
    badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    label: "تنبيه",
  },
  info: {
    icon: Info,
    cardClass: "border-blue-500/40 bg-blue-950/30",
    iconClass: "text-blue-400",
    badgeClass: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    label: "معلومة",
  },
};

const TYPE_ICONS = {
  loss_sale: TrendingDown,
  item_below_cost: TrendingDown,
  large_discount: AlertTriangle,
  low_margin: AlertCircle,
  free_item: AlertTriangle,
  product_below_cost: AlertCircle,
};

export default function SmartAlertsWidget() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ critical: 0, warning: 0, info: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/insights/anomalies?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data.alerts || []);
      setCounts(res.data.counts || { critical: 0, warning: 0, info: 0 });
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const total = counts.critical + counts.warning + counts.info;

  // Don't show if everything is fine
  if (!loading && total === 0) {
    return (
      <Card className="bg-emerald-950/20 border-emerald-500/30" data-testid="smart-alerts-clean">
        <CardContent className="p-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-emerald-400" />
          <div className="flex-1">
            <p className="text-emerald-300 font-bold text-sm">كل شي تمام 👌</p>
            <p className="text-emerald-400/70 text-xs">
              لم يتم اكتشاف أي مخالفات مالية بآخر 30 يوم
            </p>
          </div>
          <Button onClick={load} size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-500/10" data-testid="smart-alerts-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const visibleAlerts = showAll ? alerts : alerts.slice(0, 3);

  return (
    <Card className="bg-slate-800/80 border-slate-700" data-testid="smart-alerts-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Sparkles className="w-5 h-5 text-amber-400" />
            تنبيهات ذكية ({total})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="ghost" size="sm" className="text-slate-400 hover:text-white h-7" data-testid="smart-alerts-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button
              onClick={() => setExpanded(!expanded)}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white h-7"
              data-testid="smart-alerts-toggle"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        {/* Severity badges */}
        <div className="flex items-center gap-2 mt-1">
          {counts.critical > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full border bg-red-500/15 text-red-300 border-red-500/30 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {counts.critical} حرج
            </span>
          )}
          {counts.warning > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/30 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {counts.warning} تنبيه
            </span>
          )}
          {counts.info > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-500/15 text-blue-300 border-blue-500/30 flex items-center gap-1">
              <Info className="w-3 h-3" />
              {counts.info} معلومة
            </span>
          )}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-2 max-h-[480px] overflow-y-auto">
          {loading && alerts.length === 0 ? (
            <div className="py-6 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" />
            </div>
          ) : (
            visibleAlerts.map((alert) => {
              const sty = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
              const Icon = TYPE_ICONS[alert.type] || sty.icon;
              return (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${sty.cardClass}`}
                  data-testid={`alert-${alert.type}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-md ${sty.badgeClass}`}>
                      <Icon className={`w-4 h-4 ${sty.iconClass}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <h4 className="font-bold text-white text-sm">{alert.title}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sty.badgeClass}`}>
                          {sty.label}
                        </span>
                      </div>
                      <p className="text-slate-300 text-xs mt-1 leading-relaxed">{alert.description}</p>
                      {alert.amount > 0 && alert.type !== "low_margin" && (
                        <p className="text-[11px] text-slate-400 mt-1.5 font-mono">
                          القيمة: {formatCurrency(alert.amount)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {alerts.length > 3 && !showAll && (
            <Button
              onClick={() => setShowAll(true)}
              variant="ghost"
              size="sm"
              className="w-full text-emerald-400 hover:bg-emerald-500/10"
              data-testid="show-all-alerts"
            >
              عرض كل التنبيهات ({alerts.length - 3} المزيد)
            </Button>
          )}

          {showAll && alerts.length > 3 && (
            <Button
              onClick={() => setShowAll(false)}
              variant="ghost"
              size="sm"
              className="w-full text-slate-400 hover:bg-slate-700/50"
            >
              عرض أقل
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}
